import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchServiceAgreementDropdownData,
  bulkTransferServiceGrid,
  deleteServiceGridView,
  fetchInputCostShareData,
  postServiceData,
  updatedServiceGrid,
  updateServiceGridView,
  getFarmtypes,
  updateLandlordCostShare,
  filterExcelData,
  getFieldMappings,
  getCalendarMonths,
  getCropYears,
  getLandlordRateTypeMappings,
  saveLandlordRateTypeMapping,
} from "../services/serviceagreements.api";
import {
  CropTypeDropdownItem,
  InputCostData,
  JdOpsOperationTypeDropdownItem,
  LandlordDropdownItem,
  OperationType1DropdownItem,
  OperationType2DropdownItem,
  ProductCategoryDropdownItem,
} from "../types/serviceagreements";
import { FieldMapping, Option } from "../screens/ServiceAgreements/types";
import { mapOptionsToArray } from "../utils/mapDropDownOptions";
interface ServiceAgreementState {
  serviceAgreementList: [];
  landlord: LandlordDropdownItem[];
  cropType: CropTypeDropdownItem[];
  operationType1: OperationType1DropdownItem[];
  operationType2: OperationType2DropdownItem[];
  jdOpsOperationType: JdOpsOperationTypeDropdownItem[];
  productorcategory: ProductCategoryDropdownItem[];
  loading: boolean;
  error: string | null;
  grid: [];
  responsebool: boolean;
  deleteresonse: boolean;
  approveresponse: boolean;
  modifyresponse: boolean;
  postresponse: any;
  inputCostShareList: InputCostData[];
  farmShares: any;
  farmTypes: Option[];
  fieldMappingArray: FieldMapping[];
  calendarMonths: Option[];
  calendarYears: number[];
  cropYears: Option[];
  rateTypeMappings: any[];
}
const initialState: ServiceAgreementState = {
  serviceAgreementList: [],
  landlord: [],
  cropType: [],
  operationType1: [],
  operationType2: [],
  jdOpsOperationType: [],
  productorcategory: [],
  loading: false,
  error: null,
  grid: [],
  responsebool: false,
  deleteresonse: false,
  approveresponse: false,
  modifyresponse: false,
  postresponse: [],
  inputCostShareList: [],
  farmShares: {},
  farmTypes: [],
  fieldMappingArray: [],
  calendarMonths: [],
  calendarYears: [],
  cropYears: [],
  rateTypeMappings: [],
};

export const fetchServiceAgreementDropdown = createAsyncThunk(
  "dropdown/fetch",
  async (dropdownType: string) => {
    const response = await fetchServiceAgreementDropdownData(dropdownType);
    return response.data;
  }
);

export const postProductServiceDropdown = createAsyncThunk(
  "productservicedropdown/post",
  async (value: any) => {
    const response = await postServiceData(value);
    return response.data;
  }
);

export const postUpdateAgreementCropView = createAsyncThunk(
  "updateLandlordCostShare",
  async (value: any) => {
    const response = await updateLandlordCostShare(value);
    return response.data;
  }
);

export const landLordToFieldMapping = createAsyncThunk(
  "landlordFieldMapping",
  async () => {
    const response = await getFieldMappings();
    return response.data;
  }
);

export const getCalendarYears = createAsyncThunk(
  "getCalendarYears",
  async () => {
    const response = await getCropYears();
    return response.data;
  }
);

export const getCalendarMonth = createAsyncThunk(
  "getCalendarMonth",
  async (value: number) => {
    const response = await getCalendarMonths(value);
    return response.data;
  }
);

export const fetchAllFarmTypes = createAsyncThunk(
  "fetchFarmTypes",
  async () => {
    const response = await getFarmtypes();
    return response.data;
  }
);

export const fetchFilteredLandLordShare = createAsyncThunk(
  "ServiceAgreement/FilterLandLordCostShare",
  async (value: any) => {
    const response = await filterExcelData(value);
    return response.data;
  }
);

export const updateProductServiceGrid = createAsyncThunk(
  "productservicegrid/update",
  async (value: any) => {
    const response = await updateServiceGridView(value);
    return response.data;
  }
);

export const updatedProductServiceGrid = createAsyncThunk(
  "productservicegrid/updated",
  async (value: any) => {
    const response = await updatedServiceGrid(value);
    return response.data;
  }
);

export const fetchInputCostShare = createAsyncThunk(
  // api data
  "inputcostshare/fetch",
  async (year: number) => {
    const response = await fetchInputCostShareData(year);
    return response.data;
  }
);
export const bulkTransferServiceGridData = createAsyncThunk(
  "bulkservicegrid/update",
  async (value: any) => {
    const response = await bulkTransferServiceGrid(value);
    return response.data;
  }
);

export const deleteProductServiceGrid = createAsyncThunk(
  "productservicegrid/delete",
  async (value: any) => {
    const response = await deleteServiceGridView(value);
    return response.data;
  }
);

export const getRateTypeMappings = createAsyncThunk(
  "getRateTypeMappings",
  async () => {
    const response = await getLandlordRateTypeMappings();
    return response.data;
  }
);

export const saveRateTypeMapping = createAsyncThunk(
  "saveRateTypeMapping",
  async (data: any) => {
    const response = await saveLandlordRateTypeMapping(data);
    return response.data;
  }
);

const serviceAgreementSlice = createSlice({
  name: "serviceAgreement",
  initialState,
  reducers: {
    resetResponseBool: (state) => {
      state.responsebool = false;
    },
    resetapproveresponse: (state) => {
      state.approveresponse = false;
    },
    resetmodifyresponse: (state) => {
      state.approveresponse = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceAgreementDropdown.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServiceAgreementDropdown.fulfilled, (state, action) => {
        state.loading = false;
        if (action.meta.arg === "landlord") {
          state.landlord = action.payload;
        } else if (action.meta.arg === "cropType") {
          state.cropType = action.payload;
        } else if (action.meta.arg === "jdOpsOperationType") {
          state.jdOpsOperationType = action.payload;
        } else if (action.meta.arg === "productorcategory") {
          state.productorcategory = action.payload;
        }
      })
      .addCase(fetchServiceAgreementDropdown.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch dropdown data.";
      })
      .addCase(fetchInputCostShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInputCostShare.fulfilled, (state, action) => {
        state.loading = false;
        state.inputCostShareList = action.payload;
      })
      .addCase(fetchAllFarmTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.farmTypes = action.payload;
      })
      .addCase(fetchFilteredLandLordShare.fulfilled, (state, action) => {
        state.loading = false;
        state.farmShares = action.payload;
      })
      .addCase(fetchFilteredLandLordShare.rejected, (state) => {
        state.loading = false;
        state.error = "Failed to fetch data.";
      })
      .addCase(postProductServiceDropdown.fulfilled, (state, action) => {
        state.loading = false;
        state.responsebool = !state.responsebool;
        state.postresponse = action.payload;
      })
      .addCase(deleteProductServiceGrid.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteresonse = action.payload;
      })
      .addCase(updatedProductServiceGrid.fulfilled, (state) => {
        state.loading = false;
        state.approveresponse = !state.approveresponse;
      })
      .addCase(updateProductServiceGrid.fulfilled, (state) => {
        state.loading = false;
        state.modifyresponse = !state.modifyresponse;
      })
      .addCase(landLordToFieldMapping.fulfilled, (state, action) => {
        state.loading = false;
        state.fieldMappingArray = action.payload;
      })
      .addCase(getCalendarMonth.fulfilled, (state, action) => {
        state.loading = false;
        state.calendarMonths = action.payload;
      })
      .addCase(getCalendarYears.fulfilled, (state, action) => {
        state.loading = false;
        state.calendarYears = mapOptionsToArray(action.payload);
      })
      .addCase(getRateTypeMappings.fulfilled, (state, action) => {
        state.loading = false;
        state.rateTypeMappings = action.payload;
      });
  },
});

export default serviceAgreementSlice.reducer;
export const { resetResponseBool, resetapproveresponse, resetmodifyresponse } = serviceAgreementSlice.actions;